import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { usePopupState, bindTrigger } from 'material-ui-popup-state/hooks';
import { history } from '../../';

import { isWidthDown } from '@material-ui/core/withWidth';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

import HelpIcon from '@material-ui/icons/HelpOutline';
import FeedbackIcon from '@material-ui/icons/Feedback';

import pushToGTM, {
	CLICK_ABOUT_GA4_TAG,
	CLICK_FEEDBACK_GA4_TAG,
	CLICK_LOGIN_GA4_TAG,
	CLICK_LOGOUT_GA4_TAG
} from '../../utils/analytics/gtm';
import { useStyles } from './styles';
import { tabRoutes } from '../../routes';
import Button from '../Button';
import Link from '../Link';
import MyDesignsDialog from './MyDesignsDialog';
import AboutDialog from './AboutDialog';
import UserMenu from './UserMenu';
import MyDesignsPopover from './MyDesignsPopover/MyDesignsPopover';
import { COMMUNITY_FORUM } from '../../utils/constants';
import { localView } from '../../utils/analytics/local';
import { useTranslation } from 'react-i18next';

const Navbar = ({ screenWidth, appVersion, logIn, logOut }) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const [myDesignsOpen, setMyDesignsOpen] = useState(false);
	const [aboutOpen, setAboutOpen] = useState(false);
	const inputsRoute = tabRoutes.find(route => route.path === '/inputs');
	const userLoggedIn = useSelector(state => state.user.loggedIn);
	const userId = useSelector(state => state.user.id);
	const activeTab = useSelector(state => state.tabbar.activeTab);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const partNumber = useSelector(state => state.selectedDesign.detail.rankingParameters.partNumber);

	const myDesignsPopupState = usePopupState({
		variant: 'popover',
		popupId: 'my-designs-popup'
	});
	const isInputTabActive = () => activeTab === inputsRoute.tabId;
	const isUserLoggedIn = userLoggedIn && userId !== null;

	const handleLogin = login => {
		if (login) {
			pushToGTM(CLICK_LOGIN_GA4_TAG);

			logIn();
		} else {
			pushToGTM(CLICK_LOGOUT_GA4_TAG);

			logOut();
		}

		// SetDrawerOpened(false);
	};

	const handleNewDesignClick = () => {
		history.push(inputsRoute.path);
	};

	return (
		<div className={classes.root}>
			<AppBar position="fixed" elevation={0} className={classes.appbar}>
				<Toolbar className={classes.toolbar}>
					<div className={classes.leftContainer}>
						<Link
							noWrap
							variant={isWidthDown('xs', screenWidth) ? 'subtitle2' : 'h6'}
							underline="none"
							href={window.location.href}
							target="_self">
							<span className={classes.mainTitle}>WebDesigner+&trade; Power Supply</span>
						</Link>
					</div>

					{isWidthDown('sm', screenWidth) ? (
						<UserMenu
							handleNewDesign={handleNewDesignClick}
							handleMyDesigns={() => isUserLoggedIn && setMyDesignsOpen(true)}
							handleAbout={() => setAboutOpen(true)}
							handleFeedback={() => setFeedbackOpen(true)}
							handleLogin={handleLogin}
							isUserLoggedIn={isUserLoggedIn}
							isInputTabActive={isInputTabActive}
						/>
					) : (
						<Grid container direction="row" alignItems="center" justify="center" className={classes.actionsContainer}>
							{!isInputTabActive() && (
								<Grid item>
									<Button
										color="inherit"
										size="small"
										disabled={progressActive}
										onClick={() => {
											localView('New Design', partNumber);
											handleNewDesignClick();
										}}
										className={classes.focusableIconButton}>
										<span className={classes.title}>{t('components.navbar.buttons.newDesign')}</span>
									</Button>
								</Grid>
							)}

							<Grid item>
								<Button
									size="small"
									disabled={progressActive}
									onClick={() => {
										localView('My Design', partNumber);
										isUserLoggedIn && setMyDesignsOpen(true);
									}}
									{...(!isUserLoggedIn && bindTrigger(myDesignsPopupState))}
									aria-label="My Designs"
									className={classes.focusableIconButton}>
									<span className={classes.title}>{t('components.navbar.buttons.myDesigns')}</span>
								</Button>
							</Grid>

							<Grid item>
								<Tooltip title={t('components.navbar.tooltipTexts.about')}>
									<IconButton
										color="inherit"
										disabled={progressActive}
										onClick={() => {
											localView('About', partNumber);
											pushToGTM(CLICK_ABOUT_GA4_TAG);
											setAboutOpen(true);
										}}
										className={classes.focusableIconButton}>
										<HelpIcon />
									</IconButton>
								</Tooltip>
							</Grid>

							<Grid item>
								<Tooltip title={t('components.navbar.tooltipTexts.feedback')}>
									<IconButton
										color="inherit"
										disabled={progressActive}
										href={COMMUNITY_FORUM.url}
										target="_blank"
										onClick={() => {
											localView('Submit feedback', partNumber);
											pushToGTM(CLICK_FEEDBACK_GA4_TAG);
										}}
										className={classes.focusableIconButton}>
										<FeedbackIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
					)}

					{isUserLoggedIn ? (
						<MyDesignsDialog open={myDesignsOpen} setOpen={setMyDesignsOpen} />
					) : (
						<MyDesignsPopover popupState={myDesignsPopupState} handleLogIn={() => handleLogin(true)} />
					)}

					<AboutDialog open={aboutOpen} setOpen={setAboutOpen} appVersion={appVersion} />
				</Toolbar>
			</AppBar>
		</div>
	);
};

Navbar.propTypes = {
	screenWidth: PropTypes.number.isRequired,
	appVersion: PropTypes.string.isRequired,
	logIn: PropTypes.func.isRequired,
	logOut: PropTypes.func.isRequired
};

export default Navbar;
